import { createFileRoute } from "@tanstack/react-router";
import {
  listParam,
  listValue,
  optionParam,
  textParam,
} from "@/utils/searchParams";
import { TOOL_AVAILABILITIES } from "@/utils/availability";
import { ENTRY_OWNERS, type EntryOwnerFilter } from "@/utils/toolFilters";
import type { ToolAvailability } from "@/hooks/types";

// The search and filters live in the URL, so opening a tool (or the importer)
// and coming back leaves the list where the admin had narrowed it (NAV-45).

/** What the dropdowns offer. Re-exported rather than used inline so the list
 *  the page renders and the list this route accepts are the same object — see
 *  `toolFilters.test.ts` for what drifts otherwise. */
export const BU_TOOL_AVAILABILITIES: readonly ToolAvailability[] =
  TOOL_AVAILABILITIES;
export const BU_TOOL_OWNERS: readonly EntryOwnerFilter[] = ENTRY_OWNERS;

/**
 * The two halves of a business unit's tool management (Jira NAV-147): what it has
 * decided, and what is still running on the group's default.
 *
 * In the URL like every other control on this page, so an admin working
 * through the queue can open a tool, come back, and still be on it — and so
 * the queue is a link somebody can be sent.
 */
export const BU_TOOL_TABS = ["settled", "unsettled"] as const;
export type BuToolsTab = (typeof BU_TOOL_TABS)[number];

export type BuToolsSearch = {
  q?: string;
  /** Absent means the settled list, so the page's own address stays the plain
   *  one it always was. */
  tab?: BuToolsTab;
  /** Category *names*, comma-separated — data rather than a fixed list.
   *  Several at once, meaning "any of these" (Jira NAV-109), in the one `cat`
   *  parameter it always was, so a single-category address still reads the
   *  same. A string for the reason the group catalogue's own `cat` gives. */
  cat?: string;
  /** What this business unit's members meet for the tool: its own availability where
   *  it has one, the group's default on the entry otherwise (ADR 0024). */
  availability?: ToolAvailability;
  owner?: EntryOwnerFilter;
};

export const Route = createFileRoute("/bu-admin/tools")({
  validateSearch: (search: Record<string, unknown>): BuToolsSearch => ({
    q: textParam(search.q),
    tab: optionParam(BU_TOOL_TABS, search.tab),
    cat: listValue(listParam(search.cat) ?? []),
    availability: optionParam(BU_TOOL_AVAILABILITIES, search.availability),
    owner: optionParam(BU_TOOL_OWNERS, search.owner),
  }),
});
